import React, { useEffect, useMemo, useContext, useRef } from 'react';

import { useDispatch } from 'react-redux';
import { Option } from 'ia-react-core/~/interfaces/Option';
import { ManualAddress } from 'ia-react-core/~/interfaces/ManualAddress';
import { StringKeyOf } from 'ia-react-core/~/interfaces/StringKeyOf';
import getLanguageCode from '../../../utils/language/getLanguageCode';
import useScopedSelector from '../../../hooks/useScopedSelector';
import FormRow from '../FormRow';
import FormCol from '../FormCol';
import TextControl, { TextControlProps } from '../TextControl';
import DropdownControl, { DropdownControlProps } from '../DropdownControl';
import PostalCodeControl from '../PostalCodeControl';
import USPostalCodeControl from '../USPostalCodeControl';
import FormContext from '../FormContext';
import UnionControlGroup from '../UnionControlGroup';
import defaultDeliveryOptions from '../../../constants/DELIVERY_TYPES';
import defaultSubBuildingOptions from '../../../constants/SUB_BUILDING_TYPES';
import Collapse from '../../collapses/Collapse';
import useTranslation from '../../../hooks/useTranslation';
import openCollapseByName from '../../../actions/collapse-fn/openCollapseByName';
import { FormProps } from '../Form';
import TextInput from '../TextInput';

const maxBuildingNumberLength = 8;
const maxSubBuildingNumberLength = 6;
const maxStreetTextLength = 30;
const maxCityTextLength = 30;
const maxPlaceTextLength = 20;

export interface ManualAddressFormProps<
  CustomAddress extends object = ManualAddress,
  AddressKey extends keyof CustomAddress = keyof CustomAddress
> extends FormProps<CustomAddress> {
  initialManualEntry?: boolean;
  autoFocus?: boolean;
  /** Array of countries. */
  countries?: Option[];
  /** Array of provinces to display as a dropdown. Used when the "provincesCountry" matches the current country. */
  provinces?: Option[];
  /** Array of states. Used as the US "provinces" when provided. Alternative to passing "provinces" prop with provincesCountry="US". */
  states?: Option[];
  /** Array of sub building. */
  subBuildingOptions?: Option[];
  /** Array of deliveryOptions. */
  deliveryOptions?: Option[];
  /** The building number field name if you want to override the default name */
  buildingNumberName?: AddressKey;
  /** The street field name if you want to override the default name */
  streetName?: AddressKey;
  /** The subBuilding field name if you want to override the default name */
  subBuildingName?: AddressKey;
  /** The subBuildingType field name if you want to override the default name */
  subBuildingTypeName?: AddressKey;
  /** The city field name if you want to override the default name */
  cityName?: AddressKey;
  /** The country field name if you want to override the default name */
  countryName?: AddressKey;
  /** The province field name if you want to override the default name */
  provinceName?: AddressKey;
  /** The postalCode field name if you want to override the default name */
  postalCodeName?: AddressKey;
  /** The place field name if you want to override the default name */
  placeName?: AddressKey;
  /** The deliveryType field name if you want to override the default name */
  deliveryTypeName?: AddressKey;
  /** The poBox field name if you want to override the default name */
  poBoxNumberName?: AddressKey;
  /** The ruralRoute field name if you want to override the default name */
  ruralRouteName?: AddressKey;
  /** Indicates which country the provinces are for. If the provincesCountry matches the current selected country, the "provinces" prop will be used. */
  provincesCountry?: string | null;
  /** Hide the subbuilding fields. */
  hideSubBuilding?: boolean;
  /** Hide the subbuilding type field. */
  hideSubBuildingType?: boolean;
  /** Hide the province fields. */
  hideProvince?: boolean;
  /** Set the provinces as loading. */
  loadingProvinces?: boolean;
  /** Props for the "buildingNumber" control */
  buildingNumberProps?: TextControlProps;
  /** Props for the "street" control */
  streetProps?: TextControlProps;
  /** Props for the "subBuildingType" control */
  subBuildingTypeProps?: DropdownControlProps<string, string>;
  /** Props for the "subBuilding" control */
  subBuildingProps?: TextControlProps;
  /** Props for the "city" control */
  cityProps?: TextControlProps;
  /** Props for the "country" control */
  countryProps?: DropdownControlProps<string, string>;
  /** Props for the "province" control */
  provinceProps?: DropdownControlProps<string, string>;
  /** Props for the "postalCode" control */
  postalCodeProps?: TextControlProps;
  /** Props for the "postalCode" control */
  placeProps?: TextControlProps;
  /** Props for the "deliveryType" control */
  deliveryTypeProps?: DropdownControlProps<string, string>;
  /** Props for the "poBoxNumber" control */
  poBoxNumberProps?: TextControlProps;
  /** Props for the "ruralRoute" control */
  ruralRouteProps?: TextControlProps;
  /** @ignore */
  name?: string;
}

const ManualAddressFormDefaultProps: Partial<ManualAddressFormProps<any, any>> = {
  name: 'address',
  provincesCountry: 'CA',
  streetName: 'street',
  countryName: 'countryIso2',
  provinceName: 'province',
  buildingNumberName: 'buildingNumber',
  subBuildingTypeName: 'subBuildingType',
  subBuildingName: 'subBuilding',
  cityName: 'city',
  postalCodeName: 'postalCode',
  poBoxNumberName: 'poBoxNumber',
  ruralRouteName: 'ruralRoute',
  deliveryTypeName: 'deliveryType',
  placeName: 'place',
  autoFocus: false,
  hideSubBuilding: false,
  hideSubBuildingType: false,
  hideProvince: false,
  loadingProvinces: false,
};

const ManualAddressForm = <
  CustomAddress extends object = ManualAddress,
  AddressKey extends StringKeyOf<CustomAddress> = StringKeyOf<CustomAddress>
>({
    autoFocus, provinces, provincesCountry, states, countries, valuesDst, placeName, deliveryTypeName, poBoxNumberName, ruralRouteName, deliveryOptions, buildingNumberName, streetName, postalCodeName, subBuildingName, cityName, countryName, provinceName, hideSubBuilding, hideSubBuildingType, hideProvince, loadingProvinces, subBuildingTypeName, subBuildingOptions, name, defaultValues = {}, buildingNumberProps, streetProps, subBuildingTypeProps, subBuildingProps, cityProps, countryProps, provinceProps, postalCodeProps, placeProps, deliveryTypeProps, poBoxNumberProps, ruralRouteProps,
  }: ManualAddressFormProps<CustomAddress, AddressKey>) => {
  const dispatch = useDispatch();
  const { t, language } = useTranslation('Core.ManualAddressForm');
  const languageCode = useMemo(() => getLanguageCode(language), [language]);
  const { setControlValue } = useContext(FormContext);
  const initialRenderRef = useRef(true);
  const countryCode = useScopedSelector(`${valuesDst}.${countryName}`, defaultValues[countryName]);
  const storedProvince = useScopedSelector(`${valuesDst}.${provinceName}`, undefined);
  const place = useScopedSelector(`${valuesDst}.${placeName}`, defaultValues[placeName]);
  const poBoxNumber = useScopedSelector(`${valuesDst}.${poBoxNumberName}`, defaultValues[poBoxNumberName]);
  const ruralRoute = useScopedSelector(`${valuesDst}.${ruralRouteName}`, defaultValues[ruralRouteName]);
  const deliveryType = useScopedSelector(`${valuesDst}.${deliveryTypeName}`, defaultValues[deliveryTypeName]);
  const subBuildingType = useScopedSelector(`${valuesDst}.${subBuildingTypeName}`, defaultValues[subBuildingTypeName]);
  const subBuilding = useScopedSelector(`${valuesDst}.${subBuildingName}`, defaultValues[subBuildingName]);

  const resolvedSubBuildingOptions = useMemo(() => subBuildingOptions || defaultSubBuildingOptions[languageCode], [languageCode, subBuildingOptions]);
  const resolvedDeliveryOptions = useMemo(() => deliveryOptions || defaultDeliveryOptions[languageCode], [deliveryOptions, languageCode]);
  const withSubBuilding = useMemo(() => (
    subBuildingType === resolvedSubBuildingOptions[0].value ||
    subBuildingType === resolvedSubBuildingOptions[1].value
  ), [resolvedSubBuildingOptions, subBuildingType]);
  const withDelivery = useMemo(() => (
    deliveryType === resolvedDeliveryOptions[0].value ||
    deliveryType === resolvedDeliveryOptions[1].value
  ), [resolvedDeliveryOptions, deliveryType]);

  const additionalCountryProps = useMemo(
    () => countries?.length === 1 ?
      { defaultValue: countries[0].value, readOnly: true } :
      {},
    [countries],
  );
  const resolvedProvinces = useMemo(() => (
    (countryCode === 'US' && states) ? states : provinces
  ), [countryCode, provinces, states]);

  const additionalProvinceProps = useMemo(
    () => resolvedProvinces?.length === 1 ?
      { defaultValue: resolvedProvinces[0].value, readOnly: true } :
      {},
    [resolvedProvinces],
  );

  const defaultSubBuildingType = useMemo(() => {
    if ((subBuilding as string)?.match(/(suite|ste|st|unit|unite)[ 0-9]?/i)) return resolvedSubBuildingOptions[1].value;
    if (subBuilding) return resolvedSubBuildingOptions[0].value;
    return resolvedSubBuildingOptions[2].value;
  }, [resolvedSubBuildingOptions, subBuilding]);

  const defaultDeliveryType = useMemo(() => {
    if (poBoxNumber) return resolvedDeliveryOptions[0].value;
    if (ruralRoute) return resolvedDeliveryOptions[1].value;
    return resolvedDeliveryOptions[2].value;
  }, [poBoxNumber, resolvedDeliveryOptions, ruralRoute]);
  const prevCountryCodeRef = useRef(countryCode);

  useEffect(() => {
    // Only update the session storage if the country code has actually changed
    // and it's not the initial render
    if (prevCountryCodeRef.current !== countryCode) {
      localStorage.setItem('prevCountryCode', prevCountryCodeRef.current as string); // Store the previous country code
      prevCountryCodeRef.current = countryCode; // Update the ref to the current country code
    }
  }, [countryCode]);

  useEffect(() => {
    const prevCountryCode = localStorage.getItem('prevCountryCode');
    if (storedProvince && storedProvince !== '') {
      localStorage.setItem('prevCountryCode', countryCode as string);
      if (countryCode !== prevCountryCode && !initialRenderRef.current) {
        setControlValue(provinceName, '');
      } else {
        setControlValue(provinceName, storedProvince);
        initialRenderRef.current = false;
      }
    }
  }, [countryCode, countryName, provinceName, setControlValue, storedProvince]);

  // Empty the building when selecting "none"
  useEffect(() => {
    if (subBuilding && subBuildingType === resolvedSubBuildingOptions[2].value) {
      setControlValue(subBuildingName, '');
    }
  }, [resolvedSubBuildingOptions, setControlValue, subBuilding, subBuildingName, subBuildingType]);

  // Empty the PO Box when selecting something other that "postalbox"
  useEffect(() => {
    if (poBoxNumber && (deliveryType === resolvedDeliveryOptions[1].value || deliveryType === resolvedDeliveryOptions[2].value)) {
      setControlValue(poBoxNumberName, '');
    }
  }, [deliveryType, poBoxNumber, poBoxNumberName, resolvedDeliveryOptions, setControlValue]);

  // Empty the rural route when selecting something other that "rural"
  useEffect(() => {
    if (ruralRoute && (deliveryType === resolvedDeliveryOptions[0].value || deliveryType === resolvedDeliveryOptions[2].value)) {
      setControlValue(ruralRouteName, '');
    }
  }, [deliveryType, resolvedDeliveryOptions, ruralRoute, ruralRouteName, setControlValue]);

  // Open the collapse it contains data. Values are coming from the form so are not defined on first render so the initialIsOpened cannot be used.
  useEffect(() => {
    if (place || poBoxNumber || ruralRoute) {
      dispatch(openCollapseByName(`${name}-MoreInfoCollapse`));
    }
  }, [place, poBoxNumber, ruralRoute, dispatch, name]);

  return (
    <>
      <FormRow>
        <FormCol label={t('buildingNumber')} col={4}>
          <TextControl
            name={buildingNumberName}
            autoFocus={autoFocus}
            required={!place && !poBoxNumber && !ruralRoute}
            maxLength={maxBuildingNumberLength}
            {...buildingNumberProps}
          />
        </FormCol>
        <FormCol label={t('street')} col={8}>
          <TextControl
            name={streetName}
            required={!place && !poBoxNumber && !ruralRoute}
            maxLength={maxStreetTextLength}
            {...streetProps}
          />
        </FormCol>
      </FormRow>
      {!hideSubBuilding && (
        <FormRow>
          <FormCol
            label={t('subBuilding')}
            col={12}
            minColWidth={140}
          >
            <UnionControlGroup
              split-xs={withSubBuilding ? '50%/50%' : '100%'}
              split-sm="50%/50%"
            >
              {!hideSubBuildingType && (
                <DropdownControl<string, string>
                  name={subBuildingTypeName}
                  options={resolvedSubBuildingOptions}
                  defaultValue={defaultSubBuildingType}
                  emptyValue={resolvedSubBuildingOptions[2].value}
                  {...subBuildingTypeProps}
                />
              )}
              {(withSubBuilding || hideSubBuildingType) && (
                <TextControl
                  name={subBuildingName}
                  required={!hideSubBuildingType}
                  {...subBuildingProps}
                />
              )}
            </UnionControlGroup>
          </FormCol>
        </FormRow>
      )}
      <FormRow>
        <FormCol label={t('city')} col={12}>
          <TextControl
            name={cityName}
            required
            maxLength={maxCityTextLength}
            {...cityProps}
          />
        </FormCol>
      </FormRow>
      <FormRow>
        <FormCol label={t('country')} col={6}>
          <DropdownControl
            name={countryName}
            options={countries}
            required
            {...additionalCountryProps}
            {...countryProps}
          />
        </FormCol>
        {!hideProvince && (
          <FormCol label={countryCode === 'US' ? t('state') : t('province')} col={6}>
            {!loadingProvinces && ((countryCode === provincesCountry && resolvedProvinces) || (states && countryCode === 'US')) && (
              <DropdownControl
                name={provinceName}
                options={resolvedProvinces}
                required
                {...additionalProvinceProps}
                {...provinceProps}
              />
            )}
            {(!loadingProvinces && ((countryCode !== 'CA' && countryCode !== 'US') || !resolvedProvinces)) && (
              <TextControl
                name={provinceName}
                {...provinceProps as TextControlProps}
              />
            )}
            {loadingProvinces && (
              <TextInput
                name={provinceName}
                disabled
              />
            )}
          </FormCol>
        )}
      </FormRow>
      <FormRow defaultCol={1}>
        <FormCol label={(countryCode !== 'US' ? t('postalCode') : t('zipCode'))} col={6} labelMarginBottom={8}>
          {countryCode === 'CA' && (
            <PostalCodeControl
              name={postalCodeName}
              required
              {...postalCodeProps}
            />
          )}
          {countryCode === 'US' && (
            <USPostalCodeControl
              name={postalCodeName}
              required
              {...postalCodeProps}
            />
          )}
          {countryCode !== 'US' && countryCode !== 'CA' && (
            <TextControl
              name={postalCodeName}
              required
              {...postalCodeProps}
            />
          )}
        </FormCol>
      </FormRow>
      <Collapse
        name={`${name}-MoreInfoCollapse`}
        title={t('moreInfo')}
        small
        whiteBackground
        noContentPadding
        noTargetPadding
        targetFontWeight={600}
        targetMargin="10px 0"
      >
        <FormRow>
          <FormCol label={t('place')} col={12}>
            <TextControl
              name={placeName}
              maxLength={maxPlaceTextLength}
              {...placeProps}
            />
          </FormCol>
        </FormRow>
        <FormRow>
          <FormCol
            label={t('postalBox')}
            col={12}
            minColWidth={140}
          >
            <UnionControlGroup
              split-xs={withDelivery ? '50%/50%' : '100%'}
              split-sm="50%/50%"
            >
              <DropdownControl
                name={deliveryTypeName}
                options={resolvedDeliveryOptions}
                defaultValue={defaultDeliveryType}
                emptyValue={resolvedDeliveryOptions[2].value}
                {...deliveryTypeProps}
              />
              {withDelivery && (
                <>
                  {deliveryType === resolvedDeliveryOptions[0].value && (
                    <TextControl
                      name={poBoxNumberName}
                      maxLength={maxSubBuildingNumberLength}
                      required
                      {...poBoxNumberProps}
                    />
                  )}
                  {deliveryType === resolvedDeliveryOptions[1].value && (
                    <TextControl
                      name={ruralRouteName}
                      maxLength={maxSubBuildingNumberLength}
                      required
                      {...ruralRouteProps}
                    />
                  )}
                </>
              )}
            </UnionControlGroup>
          </FormCol>
        </FormRow>
      </Collapse>
    </>
  );
};

ManualAddressForm.defaultProps = ManualAddressFormDefaultProps;

export default ManualAddressForm;
